/**
 * Narrows the care detail's pre-checked providers to the ones that apply to the
 * code being viewed.
 *
 * The care detail endpoint returns every pre-checked provider tied to the
 * code's category — the professional version and the facility version alike —
 * plus the `sidecarCodeType` of the code itself. Only providers whose
 * `categoryType` matches that type are pre-checked for this code, so a facility
 * code such as APC72158 must not surface a provider that is pre-checked only
 * for the professional code.
 *
 * When the endpoint doesn't tell us the code type there is nothing to narrow
 * by, so every provider is returned. This is the opposite of the legacy
 * `extractPreCheckedProvidersContent` in sidecar-components, which returns
 * nothing without a code type: here the type is part of the care detail
 * contract, so treating a missing one as "hide everything" would drop providers
 * that render correctly today.
 *
 * @param {Object} [precheckedProviders] - `detail.precheckedProviders` from the
 *   care detail endpoint
 * @param {Array} [precheckedProviders.providers] - Pre-checked providers for
 *   the code's category, each carrying its own `categoryType`
 * @param {'Professional' | 'Facility'} [precheckedProviders.sidecarCodeType] -
 *   The type of the code being viewed
 * @returns {Array} The providers pre-checked for this code's type
 */
export const getPreCheckedProvidersForCodeType = precheckedProviders => {
  const { providers, sidecarCodeType } = precheckedProviders ?? {}

  if (!Array.isArray(providers)) return []

  if (sidecarCodeType !== 'Professional' && sidecarCodeType !== 'Facility') {
    return providers
  }

  return providers.filter(
    provider => provider?.categoryType === sidecarCodeType,
  )
}
